import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../context/RTLContext';
import Sidebar from './Sidebar';
import api from '../../services/api';

const DashboardLayout = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isOnboardingRequired } = useOnboarding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL, direction } = useRTL();

  const endImpersonation = async () => {
    // Restore original token
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonationToken');

      // Fetch the original admin user data
      try {
        const response = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } catch (error) {
        console.error('Failed to fetch admin user data:', error);
      }

      window.location.href = '/admin';
    }
  };

  useEffect(() => {
    // Redirect to login if not authenticated after loading
    if (!loading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="modern-layout modern-flex-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="modern-spinner-lg modern-mb-md"></div>
          <div className="modern-text-muted">
            {t('common.status.loading')}
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  // If onboarding is required, render a minimal layout
  // The onboarding modal will overlay on top
  if (isOnboardingRequired) {
    return (
      <div className="modern-layout modern-flex-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="modern-spinner-lg modern-mb-md"></div>
          <p className="modern-text-muted modern-text-small">
            {t('onboarding.subtitle')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-layout" dir={direction}>
      {/* Impersonation Indicator - Floating bottom right */}
      {user?.isImpersonation && (
        <div
          className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm`}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-600">
                {t('auth.impersonation.viewingAs')}: <span className="font-medium text-gray-900">{user.email}</span>
              </span>
              <button
                onClick={endImpersonation}
                className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                {t('auth.impersonation.end')}
              </button>
            </div>
            {user.dashboardReleased === false && (
              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded w-fit">
                Dashboard not released
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sidebar - Always visible on desktop, toggle on mobile */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isHovered={isHovered}
        setIsHovered={setIsHovered}
      />

      {/* Main Content Area - Dynamic margin based on sidebar collapse and hover state */}
      <div
        className="modern-main transition-all duration-300"
        style={{
          paddingTop: user?.isImpersonation ? '60px' : '0',
          // Margin tracks pin state only; unpinned hover-expansion overlays content (no reflow).
          marginLeft: !isRTL ? (isCollapsed ? '80px' : '288px') : '0',
          marginRight: isRTL ? (isCollapsed ? '80px' : '288px') : '0'
        }}
      >
        {/* Mobile Menu Button - Fixed position for mobile */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-30 modern-btn-icon modern-btn-secondary"
          style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            background: 'var(--bg-card)'
          }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Page Content */}
        <main className="modern-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;